import type { Context } from "replo-runtime/store/ReploVariable";
import type {
  HTMLAttributesConfig,
  InlineItemsConfig,
  ItemsConfig,
  ItemsConfigType,
} from "schemas/dynamicData";

import * as React from "react";

import DynamicDataButton from "@v1/components/common/designSystem/DynamicDataButton";
import { DebouncedInput } from "@v1/components/common/designSystem/Input";
import { ProductSelector } from "@v1/components/common/designSystem/ProductSelector";
import Selectable from "@v1/components/designSystem/Selectable";
import DynamicDataValueIndicator from "@v1/components/editor/page/element-editor/components/extras/DynamicDataValueIndicator";
import ModifierGroup from "@v1/components/editor/page/element-editor/components/extras/ModifierGroup";
import useApplyComponentAction from "@v1/hooks/useApplyComponentAction";
import {
  selectDraftComponent,
  selectGetAttribute,
} from "@v1/reducers/core-reducer";
import { useEditorSelector } from "@v1/store/store";
import intersection from "lodash-es/intersection";
import { Minus, Plus, Trash } from "lucide-react";
import { isDynamicDataValue } from "replo-runtime";
import { getCurrentComponentContext } from "replo-runtime/shared/utils/context";
import { itemTypeToRenderData } from "replo-runtime/store/utils/items";
import { filterNulls } from "replo-utils/lib/array";
import { isEmpty, isNullish } from "replo-utils/lib/misc";
import { v4 as uuidv4 } from "uuid";

import { DynamicDataSelector } from "../../../DynamicDataSelector";

const getItemTypes = (context?: Context): ItemsConfigType[] => {
  const result: ItemsConfigType[] = ["inline"];
  if (context?.attributes?.["_product"]) {
    result.push("productImages");
  }
  return result;
};

export const ItemsSelector: React.FC<
  React.PropsWithChildren<{
    value?: ItemsConfig | null;
    allowedItemTypes?: ItemsConfigType[];
    onChange(value: ItemsConfig): void;
    onDelete?(): void;
    componentId?: string;
  }>
> = ({ onChange, value: itemsConfig, allowedItemTypes, onDelete }) => {
  const selectedItemType =
    itemsConfig?.type ||
    (!isEmpty(allowedItemTypes) ? allowedItemTypes[0] : undefined);
  const itemTypes = allowedItemTypes;

  const _renderItemsTypeSelect = () => {
    return (
      <Selectable
        layoutClassName="w-full"
        triggerLayoutClassName="w-full"
        value={selectedItemType}
        options={(itemTypes ?? []).map((itemType) => {
          return {
            label: itemTypeToRenderData(itemType).displayName,
            value: itemType,
          };
        })}
        onSelect={(value) => {
          // @ts-ignore
          onChange({ ...itemsConfig, type: value });
        }}
      />
    );
  };

  const _renderInlineItemsEditor = (itemsConfig: InlineItemsConfig) => {
    return (
      <ModifierGroup
        isCollapsible={false}
        title="Items"
        icon={itemsConfig?.valueType === "dynamic" ? null : Plus}
        iconAriaLabel={itemsConfig?.valueType === "dynamic" ? null : "Add Item"}
        onClick={() => {
          if (itemsConfig?.valueType === "dynamic") {
            return;
          }
          onChange({
            type: "inline",
            valueType: "string",
            values: [
              ...(itemsConfig?.values || []),
              // Note (Noah, 2021-08-29): This is undefined instead of null so
              // that dynamic data ignores it instead of converting to the string
              // "null" in the DOM
              { id: uuidv4(), value: undefined },
            ],
          });
        }}
      >
        {itemsConfig?.valueType !== "dynamic" &&
          (itemsConfig?.values ?? []).map((value, index, values) => {
            return (
              <div key={value.id} className="flex flex-row items-center">
                <StaticItemsInput
                  key={value.id}
                  value={value.value ?? ""}
                  onChange={onChange}
                  index={index}
                  values={values}
                  placeholder="Enter value..."
                />
                <Minus
                  className="m-2 cursor-pointer justify-self-end"
                  size={16}
                  onClick={(e) => {
                    e.stopPropagation();
                    onChange({
                      type: "inline",
                      valueType: "string",
                      values: filterNulls(
                        itemsConfig.values?.map((value, existingIndex) => {
                          if (existingIndex === index) {
                            return null;
                          }
                          return value;
                        }),
                      ),
                    });
                  }}
                />
              </div>
            );
          })}
      </ModifierGroup>
    );
  };

  const _renderProductSelect = () => {
    return (
      <ProductSelector
        // @ts-ignore
        selectedProductRef={itemsConfig?.productRef}
        onChange={(value) => {
          if (isNullish(value)) {
            onDelete?.();
          } else {
            onChange({
              type: "productImages",
              productRef: value,
            });
          }
        }}
        productRequestType="anyProducts"
        isMultiProducts={false}
      />
    );
  };

  const _renderHTMLAttributeSelector = (itemsConfig: HTMLAttributesConfig) => {
    const getChangeHandler = (attributeType: string, index: number) => {
      return (newValue: string) => {
        onChange({
          type: "htmlAttributes",
          values: (itemsConfig.values ?? []).map((value, existingIndex) => {
            return existingIndex === index
              ? { ...value, [attributeType]: newValue }
              : value;
          }),
        });
      };
    };

    return (
      <ModifierGroup
        isCollapsible={false}
        title="Attributes"
        icon={Plus}
        iconAriaLabel="Add Attribute"
        onClick={() => {
          onChange({
            type: "htmlAttributes",
            values: [
              ...(itemsConfig?.values ?? []),
              {
                id: uuidv4(),
                valueType: "dataset",
                key: undefined,
                value: undefined,
              },
            ],
          });
        }}
      >
        {(itemsConfig?.values ?? []).map(
          ({ id, valueType, key, value }, index) => {
            return (
              <div key={id} className="mb-3 flex flex-col gap-2">
                <div className="flex w-full items-center justify-between gap-2">
                  <DebouncedInput
                    startEnhancer={
                      valueType === "dataset" && (
                        <span className="bg-surface text-muted rounded-sm px-1 text-xs">
                          data-
                        </span>
                      )
                    }
                    value={key ?? ""}
                    placeholder="Key"
                    onValueChange={(value) => {
                      return getChangeHandler("key", index)(value);
                    }}
                  />
                  <div className="flex h-6 w-6 shrink-0 cursor-pointer items-center justify-center gap-1 rounded-sm">
                    <Trash
                      className="text-muted cursor-pointer"
                      size={14}
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange({
                          type: "htmlAttributes",
                          values: itemsConfig.values?.filter(
                            (_, existingIndex) => index !== existingIndex,
                          ),
                        });
                      }}
                    />
                  </div>
                </div>
                <div className="flex flex-row items-center justify-between gap-2">
                  {value && isDynamicDataValue(value) ? (
                    <DynamicDataSelector
                      targetType="text"
                      showAdvancedAttributes
                      initialPath={value.split(".")}
                      trigger={
                        <DynamicDataValueIndicator
                          type="other"
                          templateValue={value}
                          onRemove={() => {
                            getChangeHandler("value", index)("");
                          }}
                        />
                      }
                      onChange={(value) => {
                        getChangeHandler("value", index)(value);
                      }}
                    />
                  ) : (
                    <>
                      <DebouncedInput
                        value={value ?? ""}
                        placeholder="Value"
                        onValueChange={(value) => {
                          return getChangeHandler("value", index)(value);
                        }}
                        layoutClassName="grow"
                      />
                      <DynamicDataSelector
                        targetType="text"
                        showAdvancedAttributes
                        trigger={<DynamicDataButton />}
                        onChange={(value) => {
                          getChangeHandler("value", index)(value);
                        }}
                      />
                    </>
                  )}
                </div>
              </div>
            );
          },
        )}
      </ModifierGroup>
    );
  };
  return (
    <div className="flex flex-col gap-2">
      {itemTypes && itemTypes.length > 1 && _renderItemsTypeSelect()}
      {selectedItemType === "productImages" && _renderProductSelect()}
      {selectedItemType === "inline" &&
        // @ts-ignore
        _renderInlineItemsEditor(itemsConfig)}
      {selectedItemType === "htmlAttributes" &&
        _renderHTMLAttributeSelector(itemsConfig as HTMLAttributesConfig)}
    </div>
  );
};

export const CombinedItemsSelector: React.FC<
  React.PropsWithChildren<{
    value?: ItemsConfig | null;
    onChange(value: ItemsConfig): void;
    onDelete(): void;
    componentId?: string;
  }>
> = ({ onChange, value: itemsConfig, onDelete }) => {
  let templateValue: string | null = null;
  if (itemsConfig?.type === "productImages") {
    templateValue = "Product Images";
  } else if (
    itemsConfig?.type === "inline" &&
    itemsConfig?.valueType === "dynamic"
  ) {
    templateValue = itemsConfig.dynamicPath;
  }

  return (
    <DynamicDataSelector
      targetType="list"
      initialPath={templateValue?.split(".") ?? []}
      trigger={
        <DynamicDataValueIndicator
          type="other"
          templateValue={templateValue}
          onRemove={onDelete}
        />
      }
      onChange={(value) => {
        const transformedValue: InlineItemsConfig = {
          type: "inline",
          valueType: "dynamic",
          dynamicPath: value,
        };
        onChange(transformedValue);
      }}
    />
  );
};

const ItemsModifier: React.FC<
  React.PropsWithChildren<{
    field: string;
    title?: string;
    allowedItemTypes?: ItemsConfigType[];
  }>
> = ({ field, title, allowedItemTypes }) => {
  const draftComponent = useEditorSelector(selectDraftComponent);
  const applyComponentAction = useApplyComponentAction();
  const getAttribute = useEditorSelector(selectGetAttribute);

  if (!draftComponent) {
    return null;
  }

  const itemsConfig: ItemsConfig | undefined = getAttribute(
    draftComponent,
    field,
    {},
  ).value;

  const possibleItemTypes = getItemTypes(
    getCurrentComponentContext(draftComponent?.id),
  );
  const itemTypes = allowedItemTypes
    ? intersection(possibleItemTypes, allowedItemTypes)
    : possibleItemTypes;

  return (
    <ModifierGroup title={title || "Items"}>
      <ItemsSelector
        allowedItemTypes={itemTypes}
        value={itemsConfig}
        onChange={(newConfig) => {
          applyComponentAction({
            type: "setProps",
            value: {
              items: newConfig,
            },
          });
        }}
        onDelete={() => {
          // Note (Martin, 2022-04-29): props.field comes with a "props." prefix
          // that is not part of the name.
          const propName = field.split(".").pop();
          if (propName) {
            applyComponentAction({
              type: "deleteProps",
              propName,
            });
          }
        }}
        componentId={draftComponent.id}
      />
    </ModifierGroup>
  );
};

export default ItemsModifier;

function StaticItemsInput({
  value,
  onChange,
  values,
  index,
  placeholder,
}: {
  value: string;
  values: {
    id: string;
    value: string | undefined;
  }[];
  onChange(config: ItemsConfig): void;
  index: number;
  placeholder?: string;
}) {
  const onValueChange = React.useCallback(
    (newValue: string) => {
      const nextValues = values.map((value, existingIndex) => {
        if (existingIndex === index) {
          return { ...value, value: newValue };
        }
        return value;
      });
      onChange({
        type: "inline",
        valueType: "string",
        values: nextValues,
      });
    },
    [values, index, onChange],
  );
  return (
    <DebouncedInput
      value={value}
      onValueChange={onValueChange}
      placeholder={placeholder}
      layoutClassName="grow"
    />
  );
}
